/**
 * "Snapshot" = a point-in-time PICTURE of the dashboard as it reads now, so it
 * can be pasted straight into a deck/email/chat. Captures the content node to
 * a PNG, copies it to the clipboard, and downloads a .png as a fallback.
 * (Export CSV, separately, is the data export.)
 *
 * Attach `ref` to the dashboard's scrolling content node; call `capture()`
 * from the Snapshot button; `snapping` is true while it runs.
 */
import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { useToast } from '../../lib/toast';
import { downloadBlob } from '../../lib/download';

export function useSnapshotCapture() {
  const ref = useRef(null);
  const toast = useToast();
  const [snapping, setSnapping] = useState(false);

  const capture = async () => {
    const node = ref.current;
    if (!node || snapping) return;
    setSnapping(true);
    try {
      // Match the app background so the capture isn't transparent/odd in dark mode.
      const bg = getComputedStyle(document.body).backgroundColor || '#ffffff';
      // app-content is a scroll container, so capture its FULL scroll size and
      // force overflow visible on the clone — otherwise only the on-screen part
      // is rendered and the dashboard is cut off.
      const fullW = Math.max(node.scrollWidth, node.clientWidth);
      const fullH = Math.max(node.scrollHeight, node.clientHeight);
      const dataUrl = await toPng(node, {
        backgroundColor: bg,
        pixelRatio: 2,
        cacheBust: true,
        width: fullW,
        height: fullH,
        style: { overflow: 'visible', maxHeight: 'none', height: `${fullH}px`, width: `${fullW}px` },
      });
      const blob = await (await fetch(dataUrl)).blob();
      const name = `dashboard-snapshot-${new Date().toISOString().slice(0, 10)}.png`;

      let copied = false;
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
          copied = true;
        }
      } catch { /* clipboard blocked/unsupported — the download still works */ }

      downloadBlob(name, blob);
      toast(copied ? 'Snapshot copied to clipboard and downloaded'
        : 'Snapshot downloaded (paste-to-clipboard not supported here)',
        { variant: 'success' });
    } catch (e) {
      toast(`Could not capture the dashboard: ${e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setSnapping(false);
    }
  };

  return { ref, capture, snapping };
}
